import { cli } from "cypress";
import { config } from "./config";
import { JSX } from "solid-js";

export const clientLogoDescriptions = {
  "visit-health": "Fitcoin Store",
};

export const CSS_FILES = {
  default: "/styles/main.css",
  lazypay: "/styles/lazypay.css",
  "lazypay-mock": "/styles/lazypay.css",
  "mock-niyo": "/styles/mobikwik.css",
};

export function getCSSFile(clientIdPrefix: string): string {
  if (clientIdPrefix in CSS_FILES) {
    return CSS_FILES[clientIdPrefix as keyof typeof CSS_FILES];
  }

  return CSS_FILES.default;
}

export const FONTS = {
  default: [
    "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Krona+One&display=swap",
  ],
  rnr: ["https://fonts.googleapis.com/css2?family=Sacramento&display=swap"],
  sdk: [
    {
      "mock-niyo":
        "https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap",
    },
  ],
};

export const BRAND_L2_SUMMARY_DISCOUNT_LABEL = {
  default: "Hubble discount",
  lazypay: "Lazypay discount",
  "lazypay-mock": "Lazypay discount",
};

export function getBrandL2SummaryLabel(clientIdPrefix: string): string {
  if (clientIdPrefix in BRAND_L2_SUMMARY_DISCOUNT_LABEL) {
    return BRAND_L2_SUMMARY_DISCOUNT_LABEL[
      clientIdPrefix as keyof typeof BRAND_L2_SUMMARY_DISCOUNT_LABEL
    ];
  }

  return BRAND_L2_SUMMARY_DISCOUNT_LABEL.default;
}

type ClientIdConfig = {
  axisClientId: string;
  tdClientId: string;
  bajajClientId: string;
  godrejPropertiesClientId: string;
  moneycontrolClientId: string;
  darkThemeClients: string[];
  kotakClientId: string;
  onePercentClientId: string;
  stooferClientId: string;
};

const CLIENT_ID_CONFIGS: Record<string, ClientIdConfig> = {
  prod: {
    axisClientId: "axisbank-llE1XBk0",
    tdClientId: "tata-digital-HmS62avt",
    bajajClientId: "bajajfinserv-CyyetFBG",
    godrejPropertiesClientId: "godrej-properties-rnr-FsDd6dSt",
    moneycontrolClientId: "moneycontrol-2lABGigG",
    darkThemeClients: [
      "axisbank-llE1XBk0",
      "tata-digital-HmS62avt",
      "bajajfinserv-CyyetFBG",
      "godrej-properties-rnr-FsDd6dSt",
      "moneycontrol-2lABGigG",
    ],
    kotakClientId: "kotak-client-id-placeholder", // TODO: remove this
    onePercentClientId: "onepercent-XQfcW5F6JGRHJAXhFTbf",
    stooferClientId: "stoofers-1F9eGgKR",
  },
  nonProd: {
    axisClientId: "acme-code-3241c2c1",
    tdClientId: "tata-digital-23gvn314",
    bajajClientId: "bajaj-code-Bp3sxUCw",
    godrejPropertiesClientId: "godrej-properties-rnr-LEj5WNL5",
    moneycontrolClientId: "moneycontrol-QH53rltu",
    darkThemeClients: [
      "tata-digital-23gvn314",
      "acme-code-3241c2c1",
      "bajaj-code-Bp3sxUCw",
      "godrej-properties-rnr-LEj5WNL5",
      "moneycontrol-QH53rltu",
    ],
    kotakClientId: "kotak-sdk",
    onePercentClientId: "onepercent",
    stooferClientId: "stoofers-oPExjdmx",
  },
};

export const clientIdConfig =
  config.env === "Prod" ? CLIENT_ID_CONFIGS.prod : CLIENT_ID_CONFIGS.nonProd;

export function isDarkThemeClient(clientId: string): boolean {
  return clientIdConfig.darkThemeClients.includes(clientId);
}

export function showHowItWorksForCodeBasedPortal(clientId?: string): boolean {
  return (
    !!clientId &&
    [
      clientIdConfig.axisClientId,
      clientIdConfig.bajajClientId,
      clientIdConfig.godrejPropertiesClientId,
    ].includes(clientId)
  );
}

export function showCoinsPillOnHome(clientId: string): boolean {
  return [
    clientIdConfig.axisClientId,
    clientIdConfig.bajajClientId,
    clientIdConfig.godrejPropertiesClientId,
    clientIdConfig.moneycontrolClientId,
  ].includes(clientId);
}

export function getLoginPageIntroText(clientId?: string): string {
  if (clientId === clientIdConfig.axisClientId) {
    return "👋 Welcome to Axis rewards portal";
  } else if (clientId === clientIdConfig.tdClientId) {
    return "👋 Welcome to Tata Digital rewards portal";
  } else if (clientId === clientIdConfig.godrejPropertiesClientId) {
    return "Congratulations on your new home at Godrej Properties! 🏡";
  } else if (clientId === clientIdConfig.moneycontrolClientId) {
    return "👋 Welcome to Moneycontrol Privileges";
  }

  return "👋 Welcome";
}

export function getLoginPageIntroHeader(clientId?: string): JSX.Element {
  if (clientId === clientIdConfig.godrejPropertiesClientId) {
    return (
      <>Wishing you endless joy and beautiful memories in your new space.</>
    );
  }

  return <>We’re grateful to have you as a customer</>;
}

export function getPartnerPortalTncUrl(clientId?: string): string {
  if (clientId === clientIdConfig.axisClientId) {
    return "/tnc/axisbank";
  } else if (clientId === clientIdConfig.tdClientId) {
    return "https://www.myhubble.money/tata-neu/terms-and-conditions";
  }

  return "https://www.myhubble.money/terms-and-conditions";
}

export function showUserDataModal(clientId?: string): boolean {
  if (clientId === clientIdConfig.axisClientId) {
    return false;
  }

  return true;
}

export function isCustomCoinInputEnabled(clientId?: string): boolean {
  return clientId === clientIdConfig.kotakClientId;
}

export function shouldShowCoinDiscountAsPercentage(clientId?: string): boolean {
  return !!clientId && clientId === clientIdConfig.onePercentClientId;
}

export function showHubbleBrandingOnVoucherCard(clientId?: string): boolean {
  return clientId != clientIdConfig.stooferClientId;
}
